import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, Modal, Progress, formatDate } from "@/components/pcp";
import TrechosSection from "@/components/TrechosSection";
import MapaOS from "@/components/MapaOS";
import { VincularContratoModal } from "@/components/contrato/VincularContratoModal";
import { EditarApontamentoModal } from "@/components/EditarApontamentoModal";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Pencil, TrashSimple, MapPin, LinkSimple, Camera, Package, Warning, CheckCircle, Printer, ChartBar, Flag, FileText } from "@phosphor-icons/react";import { toast } from "sonner";

const emptyItem = {
  categoria: "Vertical", descricao: "", apelido: "", material: "", cor: "", dimensao: "",
  unidade: "un", qtd_planejada: 1, situacao: "Pendente", composicao_id: "", materiais: [],
};

// Dimensões legíveis de uma placa conforme a forma CONTRAN
const placaDim = (p) => {
  if (!p) return "";
  if (p.forma === "circulo" && p.diametro_cm) return `Ø ${p.diametro_cm} cm`;
  if (["octogono", "triangulo", "losango"].includes(p.forma) && p.lado_cm) return `lado ${p.lado_cm} cm`;
  if (p.largura_cm && p.altura_cm) return `${p.largura_cm} × ${p.altura_cm} cm`;
  return "";
};

const fmtData = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const CelFoto = ({ a, onZoom }) => (
  <td>
    {a.fotos && a.fotos.length > 0 ? (
      <img src={(a.fotos_thumb || a.fotos)[0]} alt="" loading="lazy" onClick={() => onZoom(a.fotos)} style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 3, cursor: "pointer" }} />
    ) : "—"}
  </td>
);

const CelGps = ({ a }) => (
  <td>
    {a.latitude ? (
      <a href={`https://www.google.com/maps?q=${a.latitude},${a.longitude}`} target="_blank" rel="noreferrer" style={{ fontSize: "0.72rem", color: "#0A0A0A", textDecoration: "underline" }}>Mapa</a>
    ) : (<span style={{ fontSize: "0.7rem", color: "#71717A" }}>—</span>)}
  </td>
);

const CelVer = ({ a, onEdit }) => (
  <td style={{ whiteSpace: "nowrap" }}>
    <a href={`${window.location.origin}/api/public/apontamento/${a.id}`} target="_blank" rel="noreferrer" style={{ fontSize: "0.72rem", color: "#0A0A0A", textDecoration: "underline" }}>Ver</a>
    {onEdit && <button className="btn btn-ghost btn-sm" style={{ padding: 2, marginLeft: 6 }} onClick={() => onEdit(a)} title="Corrigir apontamento (admin)" data-testid={`btn-editar-apontamento-${a.id}`}><Pencil size={13} /></button>}
  </td>
);

const CardResumo = ({ label, value, unit, destaque, testId }) => (
  <div style={{ padding: 12, background: destaque ? "#FFFBEA" : "#FAFAFA", border: `1px solid ${destaque ? "#FFCC00" : "#E4E4E7"}`, borderRadius: 3 }} data-testid={testId}>
    <div className="overline">{label}</div>
    <div className="font-mono font-semibold" style={{ fontSize: "1.4rem" }}>{value}{unit && <span style={{ fontSize: "0.8rem", color: "#71717A" }}> {unit}</span>}</div>
  </div>
);

const TabelaPlacas = ({ placas, onZoom, onEdit }) => {
  const totalPlacas = placas.reduce((s, a) => s + (a.placa?.qtd_placas || 1), 0);
  const totalArea = placas.reduce((s, a) => s + (a.placa?.area_total_m2 || 0), 0);
  const fmt = (n, d = 3) => (n || 0).toLocaleString("pt-BR", { maximumFractionDigits: d });
  return (
    <section data-testid="medicao-secao-placas" style={{ marginBottom: 28 }}>
      <h3 className="font-display font-bold" style={{ fontSize: "1.05rem", marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #0A0A0A" }}>Sinalização Vertical · Placas</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
        <CardResumo label="Apontamentos" value={placas.length} testId="placas-total-apontamentos" />
        <CardResumo label="Placas instaladas" value={fmt(totalPlacas, 0)} unit="un" testId="placas-total-un" />
        <CardResumo label="Área total de placas" value={fmt(totalArea)} unit="m²" destaque testId="placas-total-m2" />
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="pcp-table" data-testid="medicao-tabela-placas">
          <thead>
            <tr>
              <th style={{ width: 50 }}>Foto</th>
              <th>Data</th>
              <th>Km</th>
              <th>Lado</th>
              <th>Placa</th>
              <th>Categoria</th>
              <th>Dimensões</th>
              <th>Substrato</th>
              <th>Película</th>
              <th>Suporte</th>
              <th style={{ textAlign: "right" }}>Qtd</th>
              <th style={{ textAlign: "right" }}>Área m²</th>
              <th>GPS</th>
              <th>Fotos</th>
            </tr>
          </thead>
          <tbody>
            {placas.map((a) => {
              const p = a.placa || {};
              return (
                <tr key={a.id} data-testid={`placa-row-${a.id}`}>
                  <CelFoto a={a} onZoom={onZoom} />
                  <td style={{ fontSize: "0.82rem" }}>{fmtData(a.data_execucao)}</td>
                  <td style={{ fontWeight: 600 }}>{p.km || "—"}</td>
                  <td style={{ fontSize: "0.82rem" }}>{p.lado_via || "—"}</td>
                  <td><strong>{p.codigo}</strong>{p.nome ? <span style={{ fontSize: "0.78rem", color: "#52525B" }}> · {p.nome}</span> : null}</td>
                  <td style={{ fontSize: "0.82rem" }}>{p.categoria || "—"}</td>
                  <td className="font-mono" style={{ fontSize: "0.78rem" }}>{p.tamanho_padrao ? `${p.tamanho_padrao} · ` : ""}{placaDim(p) || "—"}</td>
                  <td style={{ fontSize: "0.82rem" }}>{p.substrato || "—"}</td>
                  <td style={{ fontSize: "0.82rem" }}>{p.pelicula || "—"}</td>
                  <td style={{ fontSize: "0.82rem" }}>{p.tipo_suporte || "—"}</td>
                  <td className="font-mono" style={{ textAlign: "right" }}>{fmt(p.qtd_placas || 1, 0)}</td>
                  <td className="font-mono font-semibold" style={{ textAlign: "right" }}>{fmt(p.area_total_m2)}</td>
                  <CelGps a={a} />
                  <CelVer a={a} onEdit={onEdit} />
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={10} style={{ textAlign: "right", fontWeight: 700, background: "#FAFAFA" }}>TOTAL</td>
              <td className="font-mono font-semibold" style={{ textAlign: "right", background: "#FAFAFA" }}>{fmt(totalPlacas, 0)}</td>
              <td className="font-mono font-semibold" style={{ textAlign: "right", background: "#FFFBEA" }}>{fmt(totalArea)}</td>
              <td style={{ background: "#FAFAFA" }} />
              <td style={{ background: "#FAFAFA" }} />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

export default function OrdemDetalhe() {
  // Export de medição por trecho para Excel (CSV UTF-8 com BOM — abre nativo no Excel)
  const exportarMedicaoExcel = (osDoc, aps) => {
    const byDate = (a, b) => (a.data_execucao || "").localeCompare(b.data_execucao || "");
    const ord = (aps || []).filter((a) => a.pintura && !a.deleted).sort(byDate);
    const placas = (aps || []).filter((a) => a.placa && !a.deleted).sort(byDate);
    if (ord.length === 0 && placas.length === 0) {
      toast.error("Não há apontamentos de pintura ou placas para exportar");
      return;
    }
    const totalArea = ord.reduce((s, a) => s + (a.pintura?.area_m2 || a.quantidade || 0), 0);
    const totalExt = ord.reduce((s, a) => s + (a.pintura?.extensao_m || 0), 0);
    const totalPlacas = placas.reduce((s, a) => s + (a.placa?.qtd_placas || 1), 0);
    const totalAreaPlacas = placas.reduce((s, a) => s + (a.placa?.area_total_m2 || 0), 0);

    // Formatador pt-BR
    const fmtNum = (n, casas = 2) => (n || 0).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });

    // CSV separado por ponto-e-vírgula (padrão Excel BR)
    const sep = ";";
    const esc = (v) => {
      const s = String(v ?? "");
      // Escape se contiver o separador, aspa ou quebra
      if (s.includes(sep) || s.includes('"') || s.includes("\n")) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const lines = [];
    // Cabeçalho da medição
    lines.push(esc(`MEDIÇÃO POR TRECHO · OS ${osDoc.numero}`));
    lines.push(esc(`Contratante:${sep}${osDoc.contratante || ""}`));
    lines.push(esc(`Rodovia:${sep}${osDoc.rodovia || ""} ${osDoc.trecho || ""}`.trim()));
    lines.push(esc(`Emitido em:${sep}${new Date().toLocaleString("pt-BR")}`));
    lines.push("");
    const baseUrl = window.location.origin;
    const fmtDt = (a) => {
      const dt = a.data_execucao ? new Date(a.data_execucao) : null;
      return dt ? `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}` : "";
    };
    const gps = (a) => [
      a.latitude ? a.latitude.toFixed(6).replace(".", ",") : "",
      a.longitude ? a.longitude.toFixed(6).replace(".", ",") : "",
      a.latitude ? `https://www.google.com/maps?q=${a.latitude},${a.longitude}` : "",
      `${baseUrl}/api/public/apontamento/${a.id}`,
      a.observacao || "",
    ];

    if (placas.length > 0) {
      lines.push(esc("SINALIZAÇÃO VERTICAL · PLACAS"));
      lines.push([
        "Data", "Km", "Lado", "Código", "Nome", "Categoria", "Tamanho", "Dimensões",
        "Substrato", "Película", "Suporte", "Qtd placas", "Área unit. (m²)", "Área total (m²)",
        "Latitude", "Longitude", "Google Maps", "Ver Fotos (link cliente)", "Observação",
      ].map(esc).join(sep));
      placas.forEach((a) => {
        const p = a.placa || {};
        lines.push([
          fmtDt(a), p.km || "", p.lado_via || "", p.codigo || "", p.nome || "", p.categoria || "",
          p.tamanho_padrao || "", placaDim(p), p.substrato || "", p.pelicula || "", p.tipo_suporte || "",
          fmtNum(p.qtd_placas || 1, 0), fmtNum(p.area_m2, 3), fmtNum(p.area_total_m2, 3),
          ...gps(a),
        ].map(esc).join(sep));
      });
      lines.push([esc("TOTAL PLACAS"), "", "", "", "", "", "", "", "", "", "", fmtNum(totalPlacas, 0), "", fmtNum(totalAreaPlacas, 3)].join(sep));
      lines.push("");
    }

    if (ord.length > 0) lines.push(esc("SINALIZAÇÃO HORIZONTAL · PINTURA"));
    if (ord.length > 0) lines.push([
      "Data", "Km Inicial", "Km Final", "Extensão (m)",
      "Tipo", "Método", "Cor", "Largura (cm)", "Fator",
      "Área (m²)", "Latitude", "Longitude", "Google Maps",
      "Ver Fotos (link cliente)", "Observação",
    ].map(esc).join(sep));
    ord.forEach((a) => {
      const p = a.pintura || {};
      lines.push([
        fmtDt(a),
        p.km_inicial || "",
        p.km_final || "",
        fmtNum(p.extensao_m, 0),
        p.tipo_label || p.tipo || "",
        (p.metodo || "").toUpperCase(),
        p.cor || "",
        p.largura_cm || "",
        fmtNum(p.fator, 2),
        fmtNum(p.area_m2 || a.quantidade, 2),
        ...gps(a),
      ].map(esc).join(sep));
    });
    if (ord.length > 0) lines.push([esc("TOTAL PINTURA"), "", "", fmtNum(totalExt, 0), "", "", "", "", "", fmtNum(totalArea, 2)].join(sep));

    // BOM UTF-8 para o Excel reconhecer os acentos
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Medicao_OS_${osDoc.numero || "s-numero"}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Arquivo Excel gerado");
  };
  const { id } = useParams();
  const nav = useNavigate();
  const [os, setOs] = useState(null);
  const [modal, setModal] = useState(false);
  const [contratoItens, setContratoItens] = useState([]);
  const [puxarId, setPuxarId] = useState("");
  useEffect(() => {
    if (modal && os?.contrato_id && contratoItens.length === 0) api.get(`/contratos/${os.contrato_id}`).then((r) => setContratoItens(r.data.itens || [])).catch(() => null);
    if (!modal) setPuxarId("");
  }, [modal, os?.contrato_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const puxarDoContrato = (cid) => {
    setPuxarId(cid);
    const ci = contratoItens.find((x) => x.id === cid);
    if (!ci) return;
    setItem((p) => ({ ...p, descricao: ci.descricao, apelido: ci.apelido || "", unidade: ci.unidade || p.unidade, categoria: ci.categoria && ci.categoria !== "Outros" ? ci.categoria : p.categoria, contrato_item_id: ci.id, qtd_planejada: p.qtd_planejada || "" }));
  };
  const [item, setItem] = useState(emptyItem);
  const [editId, setEditId] = useState(null);
  const [apontamentos, setApontamentos] = useState([]);
  const [composicoes, setComposicoes] = useState([]);
  const [consumoModal, setConsumoModal] = useState(false);
  const [consumo, setConsumo] = useState(null);
  const [consumoLoading, setConsumoLoading] = useState(false);
  const [concluirModal, setConcluirModal] = useState(false);
  const [devolucoes, setDevolucoes] = useState([]); // [{insumo_id, insumo_nome, unidade, quantidade, disponivel}]
  const [temDevolucao, setTemDevolucao] = useState(null); // null | true | false
  const [obsConclusao, setObsConclusao] = useState("");
  const [concluindo, setConcluindo] = useState(false);
  const [medicaoModal, setMedicaoModal] = useState(false);
  const [vincularModal, setVincularModal] = useState(false);
  const [fotoZoom, setFotoZoom] = useState(null);
  const [editAp, setEditAp] = useState(null);
  const [selAps, setSelAps] = useState([]);
  const [loteItem, setLoteItem] = useState("");
  const [loteSaving, setLoteSaving] = useState(false);
  const [apBusca, setApBusca] = useState("");
  const [apItem, setApItem] = useState("");
  const [apData, setApData] = useState("");
  const [apLimite, setApLimite] = useState(50);
  const [equipes, setEquipes] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const loadConsumo = async () => {
    setConsumoLoading(true);
    try {
      const { data } = await api.get(`/relatorios/consumo-os/${id}`);
      setConsumo(data);
    } catch (e) {
      toast.error("Erro ao carregar relatório de consumo");
    } finally {
      setConsumoLoading(false);
    }
  };

  useEffect(() => {
    if (consumoModal && !consumo) loadConsumo();
  }, [consumoModal]);

  const abrirConclusao = async () => {
    // Busca o consumo atual e pré-popula os insumos como potenciais devoluções (qtd=0)
    try {
      const { data } = await api.get(`/relatorios/consumo-os/${id}`);
      const devs = (data.insumos || []).map((i) => ({
        insumo_id: i.insumo_id,
        insumo_nome: i.insumo_nome,
        unidade: i.unidade,
        disponivel: i.consumo_real,
        quantidade: 0,
      }));
      setDevolucoes(devs);
      setTemDevolucao(null);
      setObsConclusao("");
      setConcluirModal(true);
    } catch (e) {
      toast.error("Erro ao carregar insumos da OS");
    }
  };

  const setDevQtd = (idx, val) => {
    const copy = [...devolucoes];
    copy[idx] = { ...copy[idx], quantidade: parseFloat(val) || 0 };
    setDevolucoes(copy);
  };

  const submitConclusao = async () => {
    if (temDevolucao === null) {
      toast.error("Informe se houve devolução de material");
      return;
    }
    setConcluindo(true);
    try {
      const payload = {
        devolucoes: temDevolucao === true ? devolucoes.filter((d) => (d.quantidade || 0) > 0) : [],
        observacao: obsConclusao,
      };
      await api.post(`/ordens/${id}/concluir`, payload);
      toast.success("OS concluída com sucesso!");
      setConcluirModal(false);
      // recarrega dados da OS
      const { data } = await api.get(`/ordens/${id}`);
      setOs(data);
      setConsumo(null); // força recarga do relatório
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(`Erro ao concluir OS${detail ? ": " + detail : ""}`);
    } finally {
      setConcluindo(false);
    }
  };

  const reabrirOS = async () => {
    if (!window.confirm("Reabrir esta OS? As devoluções registradas serão revertidas (débito no estoque).")) return;
    try {
      await api.post(`/ordens/${id}/reabrir`);
      toast.success("OS reaberta");
      const { data } = await api.get(`/ordens/${id}`);
      setOs(data);
      setConsumo(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(`Erro ao reabrir OS${detail ? ": " + detail : ""}`);
    }
  };

  const apsFiltrados = apontamentos.filter((a) => {
    if (apItem && a.item_id !== apItem) return false;
    if (apData && !(a.data_execucao || "").startsWith(apData) && formatDate(a.data_execucao) !== apData.split("-").reverse().join("/")) return false;
    if (!apBusca) return true;
    const q = apBusca.toLowerCase();
    const eq = equipes.find((e) => e.id === a.equipe_id)?.nome || "";
    const txt = [a.placa?.codigo, a.placa?.nome, a.placa?.km, a.placa?.lado_via, a.pintura?.km_inicial, a.pintura?.km_final, a.observacao, eq, os?.itens?.find((x) => x.id === a.item_id)?.descricao].filter(Boolean).join(" ").toLowerCase();
    return txt.includes(q);
  });

  const load = async () => {
    const [{ data: osData }, { data: ap }, comps] = await Promise.all([
      api.get(`/ordens/${id}`),
      api.get(`/apontamentos?os_id=${id}`),
      api.get(`/composicoes`).then((r) => r.data || []).catch(() => []),
    ]);
    setOs(osData);
    setApontamentos(ap);
    setComposicoes(comps);
    api.get("/equipes").then((r) => setEquipes(r.data || [])).catch(() => null);
  };

  useEffect(() => { load(); }, [id]);

  if (!os) return <div style={{ padding: 32 }}>Carregando OS…</div>;

  const applyComposicao = (compId) => {
    if (!compId) { setItem({ ...item, composicao_id: "", materiais: [] }); return; }
    const c = composicoes.find((x) => x.id === compId);
    if (!c) return;
    setItem({
      ...item,
      composicao_id: compId,
      categoria: c.categoria,
      descricao: c.descricao,
      unidade: c.unidade,
      cor: c.cor || item.cor,
      materiais: c.materiais || [],
    });
  };

  const saveItem = async () => {
    try {
      const payload = { ...item, os_id: id, qtd_planejada: parseFloat(item.qtd_planejada) || 0, composicao_id: item.composicao_id || null };
      if (editId) {
        await api.put(`/itens/${editId}`, payload);
        const orig = os.itens.find((x) => x.id === editId)?.contrato_item_id || null;
        if (os.contrato_id && (item.contrato_item_id || null) !== orig) await api.put(`/itens/${editId}/contrato-item`, { contrato_item_id: item.contrato_item_id || null });
      }
      else await api.post("/itens", payload);
      toast.success("Item salvo — materiais calculados automaticamente");
      setModal(false);
      setItem(emptyItem);
      setEditId(null);
      load();
    } catch (e) {
      toast.error("Erro ao salvar item");
    }
  };

  const removeItem = async (iid) => {
    if (!window.confirm("Remover item?")) return;
    await api.delete(`/itens/${iid}`);
    toast.success("Item removido");
    load();
  };

  const openEditItem = (i) => {
    setItem({ ...emptyItem, ...i, qtd_planejada: i.qtd_planejada, composicao_id: i.composicao_id || "", materiais: i.materiais || [] });
    setEditId(i.id);
    setModal(true);
  };

  const removeApontamento = async (ap) => {
    const it = os.itens.find((x) => x.id === ap.item_id);
    if (!window.confirm(`Excluir apontamento de ${ap.quantidade} ${it?.unidade || ""}?\n\nA quantidade será desfeita no item e o avanço da OS será recalculado.`)) return;
    try {
      await api.delete(`/apontamentos/${ap.id}`);
      toast.success("Apontamento excluído");
      load();
    } catch {
      toast.error("Erro ao excluir apontamento");
    }
  };

  return (
    <>
      <PageHeader
        testid="page-os-detail"
        title={`OS ${os.numero}`}
        subtitle={`${os.contratante} · Criada em ${formatDate(os.created_at)}`}
        actions={
          <>
            {os.status === "Concluída" ? (
              <button className="btn btn-outline" onClick={reabrirOS} data-testid="btn-reabrir-os">
                <Warning size={16} weight="bold" /> Reabrir OS
              </button>
            ) : os.status !== "Cancelada" && (
              <button className="btn btn-primary" onClick={abrirConclusao} data-testid="btn-concluir-os">
                <Flag size={16} weight="bold" /> Concluir OS
              </button>
            )}
            <button className="btn btn-outline" onClick={() => setMedicaoModal(true)} data-testid="btn-medicao-os">
              <ChartBar size={16} weight="bold" /> Medição por Trecho
            </button>
            <button className="btn btn-outline" onClick={() => setVincularModal(true)} data-testid="btn-vincular-contrato">
              <LinkSimple size={16} weight="bold" /> {os.contrato_id ? "Contrato vinculado" : "Vincular a contrato"}
            </button>
            <button className="btn btn-outline" onClick={() => setConsumoModal(true)} data-testid="btn-consumo-os">
              <ChartBar size={16} weight="bold" /> Relatório de Consumo
            </button>
            <Link to={`/ordens/${id}/bdm`} className="btn btn-primary" data-testid="btn-bdm-os">
              <FileText size={16} weight="bold" /> Boletim de Medição
            </Link>
            <Link to={`/ordens/${id}/fotos`} className="btn btn-outline" data-testid="btn-fotos-os">
              <Camera size={16} weight="bold" /> Relatório Fotográfico
            </Link>
            <Link to={`/ordens/${id}/imprimir`} className="btn btn-outline" data-testid="btn-print-os">
              <Printer size={16} weight="bold" /> Imprimir
            </Link>
            <Link to="/ordens" className="btn btn-outline"><ArrowLeft size={16} weight="bold" /> Voltar</Link>
          </>
        }
      />
      <div className="page-body">
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }} className="os-detail-grid">
          <div>
            <div className="pcp-card" style={{ padding: 20, marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 className="font-display font-bold text-lg">Itens de Serviço</h2>
                <button className="btn btn-primary btn-sm" onClick={() => { setItem(emptyItem); setEditId(null); setModal(true); }} data-testid="btn-add-item">
                  <Plus size={14} weight="bold" /> Adicionar item
                </button>
              </div>
              {os.itens?.length === 0 ? (
                <div style={{ padding: 24, color: "#71717A", textAlign: "center" }}>
                  Nenhum item cadastrado. Adicione os serviços que serão executados.
                </div>
              ) : (
                <table className="pcp-table">
                  <thead>
                    <tr>
                      <th>Categoria</th>
                      <th>Descrição</th>
                      <th style={{ textAlign: "right" }}>Planejado</th>
                      <th style={{ textAlign: "right" }}>Executado</th>
                      <th style={{ width: 160 }}>Avanço</th>
                      <th>Situação</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {os.itens.map((i) => {
                      const pct = i.qtd_planejada ? Math.min(100, (i.qtd_executada / i.qtd_planejada) * 100) : 0;
                      return (
                        <tr key={i.id}>
                          <td><span className="chip">{i.categoria}</span></td>
                          <td>
                            <div style={{ fontWeight: 600 }} data-testid={`item-nome-${i.id}`}>{i.apelido ? i.apelido : i.descricao}</div>
                            {i.apelido && <div style={{ fontSize: "0.72rem", color: "#52525B" }} title="Descrição completa da planilha">{i.descricao}</div>}
                            <div style={{ fontSize: "0.75rem", color: "#71717A" }}>
                              {[i.material, i.cor, i.dimensao].filter(Boolean).join(" · ")}
                            </div>
                          </td>
                          <td className="font-mono" style={{ textAlign: "right" }}>{i.qtd_planejada} {i.unidade}</td>
                          <td className="font-mono" style={{ textAlign: "right" }}>{i.qtd_executada.toFixed(2)}</td>
                          <td><Progress value={pct} /></td>
                          <td><StatusBadge status={i.situacao} /></td>
                          <td style={{ textAlign: "right" }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => openEditItem(i)} title="Editar item" data-testid={`btn-editar-item-${i.id}`}><Pencil size={14} weight="bold" /></button>
                            <button className="btn btn-ghost btn-sm" onClick={() => removeItem(i.id)}><TrashSimple size={14} weight="bold" color="#DC2626" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <TrechosSection osId={os.id} />

            <MapaOS apontamentos={apontamentos} />

            <div className="pcp-card" style={{ padding: 20, marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 className="font-display font-bold text-lg" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Package size={20} weight="bold" /> Materiais Necessários
                </h2>
                <span className="chip">Cálculo automático</span>
              </div>
              {(!os.materiais_necessarios || os.materiais_necessarios.length === 0) ? (
                <div style={{ padding: 20, color: "#71717A", textAlign: "center", fontSize: "0.85rem" }}>
                  Vincule uma <strong>Composição</strong> aos itens para calcular automaticamente os materiais necessários.
                </div>
              ) : (
                <table className="pcp-table">
                  <thead>
                    <tr>
                      <th>Insumo</th>
                      <th style={{ textAlign: "right" }}>Necessário (restante)</th>
                      <th style={{ textAlign: "right" }}>Disponível</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {os.materiais_necessarios.map((m, i) => {
                      const falta = m.falta;
                      const ok = falta === 0 || falta === null;
                      return (
                        <tr key={i}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{m.insumo_nome}</div>
                            <div style={{ fontSize: "0.72rem", color: "#71717A" }}>
                              Total planejado: <span className="font-mono">{m.necessario_total.toFixed(2)} {m.unidade}</span>
                            </div>
                          </td>
                          <td className="font-mono" style={{ textAlign: "right", fontWeight: 600 }}>
                            {m.necessario_restante.toFixed(2)} {m.unidade}
                          </td>
                          <td className="font-mono" style={{ textAlign: "right" }}>
                            {m.disponivel === null ? (
                              <span style={{ color: "#71717A" }}>Não cadastrado</span>
                            ) : (
                              <>
                                {m.disponivel.toFixed(2)} {m.unidade}
                                <div style={{ fontSize: "0.7rem", color: "#71717A" }}>reservado: {m.reservado_global.toFixed(2)}</div>
                              </>
                            )}
                          </td>
                          <td>
                            {m.disponivel === null ? (
                              <span className="badge badge-tratamento"><Warning size={12} weight="bold" /> Cadastrar</span>
                            ) : falta > 0 ? (
                              <span className="badge badge-aberta"><Warning size={12} weight="bold" /> Falta {falta.toFixed(2)}</span>
                            ) : (
                              <span className="badge badge-encerrada"><CheckCircle size={12} weight="bold" /> OK</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="pcp-card" style={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                <h2 className="font-display font-bold text-lg" style={{ margin: 0 }}>Apontamentos ({apsFiltrados.length}{apsFiltrados.length !== apontamentos.length ? ` de ${apontamentos.length}` : ""})</h2>
                <input className="input" style={{ width: 260 }} placeholder="Buscar: código, km, equipe, obs…" value={apBusca} onChange={(e) => { setApBusca(e.target.value); setApLimite(50); }} data-testid="ap-busca" />
                <input type="date" className="input" style={{ width: 150 }} value={apData} onChange={(e) => { setApData(e.target.value); setApLimite(50); }} data-testid="ap-data" title="Filtrar por data" />
                <select className="input" style={{ maxWidth: 360 }} value={apItem} onChange={(e) => { setApItem(e.target.value); setApLimite(50); }} data-testid="ap-filtro-item" title="Filtrar por item">
                  <option value="">Todos os itens</option>
                  {(os.itens || []).map((i) => { const n = apontamentos.filter((a) => a.item_id === i.id).length; return n > 0 ? <option key={i.id} value={i.id}>{i.codigo ? `${i.codigo} · ` : ""}{i.descricao} ({n})</option> : null; })}
                </select>
                {(apBusca || apData || apItem) && <button className="btn btn-ghost btn-sm" onClick={() => { setApBusca(""); setApData(""); setApItem(""); }} data-testid="ap-limpar">Limpar</button>}
              </div>
              {isAdmin && selAps.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px", marginBottom: 12, background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 4 }} data-testid="ap-lote-bar">
                  <strong data-testid="ap-lote-count">{selAps.length} selecionado(s)</strong>
                  <span className="text-xs" style={{ color: "#52525B" }}>mover para o item:</span>
                  <select className="input" style={{ maxWidth: 420 }} value={loteItem} onChange={(e) => setLoteItem(e.target.value)} data-testid="ap-lote-item">
                    <option value="">— escolha o item correto —</option>
                    {os.itens.map((i) => <option key={i.id} value={i.id}>{i.codigo ? `${i.codigo} · ` : ""}{i.descricao} ({i.unidade})</option>)}
                  </select>
                  <button className="btn btn-primary btn-sm" disabled={!loteItem || loteSaving} data-testid="ap-lote-mover" onClick={async () => {
                    setLoteSaving(true);
                    try {
                      const { data } = await api.post("/apontamentos/mover-item", { ids: selAps, item_id: loteItem, justificativa: "Troca de item em lote" });
                      toast.success(`${data.movidos} apontamento(s) movido(s)${data.pulados?.length ? ` · ${data.pulados.length} pulado(s): ${data.pulados.map((p) => p.motivo).join("; ")}` : ""}`);
                      setSelAps([]); setLoteItem(""); load();
                    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao mover apontamentos"); }
                    finally { setLoteSaving(false); }
                  }}>{loteSaving ? "Movendo…" : "Mover selecionados"}</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelAps([])} data-testid="ap-lote-cancelar">Cancelar</button>
                </div>
              )}
              {apontamentos.length === 0 ? (
                <div style={{ color: "#71717A", padding: 16 }}>Nenhum apontamento ainda.</div>
              ) : (
                <table className="pcp-table">
                  <thead><tr>{isAdmin && <th style={{ width: 28 }}><input type="checkbox" checked={apsFiltrados.length > 0 && apsFiltrados.slice(0, apLimite).every((a) => selAps.includes(a.id))} onChange={(e) => setSelAps(e.target.checked ? apsFiltrados.slice(0, apLimite).map((a) => a.id) : [])} title="Selecionar todos visíveis" data-testid="ap-sel-todos" /></th>}<th>Data</th><th>Item</th><th>Detalhe</th><th style={{ textAlign: "right" }}>Qtd</th><th>Observação</th><th></th></tr></thead>
                  <tbody>
                    {apsFiltrados.slice(0, apLimite).map((a) => {
                      const it = os.itens.find((x) => x.id === a.item_id);
                      return (
                        <tr key={a.id} data-testid={`ap-row-${a.id}`} style={selAps.includes(a.id) ? { background: "#FFFBEA" } : undefined}>
                          {isAdmin && <td><input type="checkbox" checked={selAps.includes(a.id)} disabled={!!a.medicao_id} title={a.medicao_id ? `Já na medição nº ${a.medicao_numero}` : "Selecionar"} onChange={(e) => setSelAps((s) => e.target.checked ? [...s, a.id] : s.filter((x) => x !== a.id))} data-testid={`ap-sel-${a.id}`} /></td>}
                          <td className="font-mono" style={{ whiteSpace: "nowrap" }}>{formatDate(a.data_execucao)} <span style={{ color: "#71717A", fontSize: "0.72rem" }}>{a.data_execucao ? new Date(a.data_execucao).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}</span></td>
                          <td style={{ fontSize: "0.8rem" }}>{it?.descricao || "—"}</td>
                          <td style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{a.placa ? <><strong>{a.placa.codigo}</strong>{a.placa.km ? ` · km ${a.placa.km}` : ""}{a.placa.lado_via ? ` · ${a.placa.lado_via}` : ""}{a.placa.diametro_cm ? ` · Ø${a.placa.diametro_cm}` : a.placa.lado_cm ? ` · lado ${a.placa.lado_cm}` : a.placa.largura_cm ? ` · ${a.placa.largura_cm}×${a.placa.altura_cm}` : ""}</> : a.pintura ? `km ${a.pintura.km_inicial || ""}${a.pintura.km_final ? ` → ${a.pintura.km_final}` : ""}` : a.tacha ? `${a.tacha.tipo || "Tacha"} · ${a.tacha.cor || ""} · ${a.tacha.posicao || ""} · km ${a.tacha.km_inicial}${a.tacha.km_final ? ` → ${a.tacha.km_final}` : ""} · cad. ${a.tacha.cadencia_m} m` : a.defensa ? (a.defensa.modo === "unidade" ? `${a.defensa.descricao_item || "Unidade"} · ${a.defensa.lado || ""} · km ${a.defensa.km_inicial} · ${a.defensa.unidades || a.quantidade} un` : `Defensa${a.defensa.vinculado_a === "abas" ? " (abas)" : ""} · ${a.defensa.lado || ""} · km ${a.defensa.km_inicial}${a.defensa.km_final ? ` → ${a.defensa.km_final}` : ""} · ${a.defensa.extensao_m || 0} m${a.defensa.abas_qtd ? ` · ${a.defensa.abas_qtd} aba(s)` : ""}`) : a.capina ? `${a.capina.vinculado_a === "limpeza" ? "Limpeza de placas" : "Capina/roçada"} · ${a.capina.lado || ""} · km ${a.capina.km_inicial}${a.capina.km_final ? ` → ${a.capina.km_final}` : ""} · ${a.capina.n_placas || 0} placa(s)${a.capina.vinculado_a === "limpeza" ? ` · ${(a.capina.placas || []).map((p) => `${p.qtd}× ${p.formato === "circular" ? `Ø${p.largura_m}` : `${p.largura_m}×${p.altura_m}`}`).join(", ")}` : ""}` : "—"}{a.fotos_count ? ` · ${a.fotos_count} foto(s)` : ""}</td>
                          <td className="font-mono" style={{ textAlign: "right" }}>{a.quantidade} {it?.unidade}</td>
                          <td style={{ fontSize: "0.8rem", color: "#52525B" }}>{a.observacao}</td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            {isAdmin && <button className="btn btn-ghost btn-sm" style={{ padding: 4, marginRight: 4 }} onClick={() => setEditAp(a)} title="Corrigir apontamento (admin)" data-testid={`btn-editar-apontamento-${a.id}`}><Pencil size={14} /></button>}
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => removeApontamento(a)}
                              title="Excluir apontamento"
                              data-testid={`btn-del-ap-${a.id}`}
                            >
                              <TrashSimple size={14} weight="bold" color="#DC2626" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {apsFiltrados.length > apLimite && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setApLimite((n) => n + 100)} data-testid="ap-mostrar-mais">Mostrar mais ({apsFiltrados.length - apLimite} restantes)</button>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="pcp-card" style={{ padding: 20, marginBottom: 24 }}>
              <div className="overline mb-2">Status</div>
              <div style={{ marginBottom: 16 }}><StatusBadge status={os.status} /></div>
              <div className="overline mb-2">Avanço geral</div>
              <Progress value={os.avanco || 0} />
            </div>

            <div className="pcp-card" style={{ padding: 20 }}>
              <div className="overline mb-3">Informações</div>
              <Info label="Rodovia" value={os.rodovia || "—"} />
              <Info label="Trecho" value={os.trecho || "—"} icon={MapPin} />
              <Info label="Município" value={os.municipio || "—"} />
              <Info label="Data de emissão" value={formatDate(os.data_emissao)} />
              <Info label="Previsão de início" value={formatDate(os.data_inicio_producao)} />
              <Info label="Previsão de término" value={formatDate(os.prazo_conclusao)} />
              {os.google_drive_link && (
                <div style={{ marginTop: 12 }}>
                  <a href={os.google_drive_link} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm btn-block">
                    <LinkSimple size={14} weight="bold" /> Abrir Google Drive
                  </a>
                </div>
              )}
              {os.observacoes && (
                <div style={{ marginTop: 16 }}>
                  <div className="overline mb-2">Observações</div>
                  <div style={{ fontSize: "0.85rem", color: "#52525B" }}>{os.observacoes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Editar item" : "Novo item de serviço"} size="lg">
        <div style={{ padding: 12, background: "#FFFCEA", border: "1px solid #FFCC00", borderRadius: 3, marginBottom: 16 }}>
          <div className="overline mb-1" style={{ color: "#0A0A0A" }}>Composição / Ficha Técnica</div>
          <select
            className="select"
            value={item.composicao_id || ""}
            onChange={(e) => applyComposicao(e.target.value)}
            data-testid="item-composicao"
          >
            <option value="">— Sem composição (preencher manualmente) —</option>
            {composicoes.map((c) => (
              <option key={c.id} value={c.id}>
                [{c.categoria}] {c.descricao} ({c.unidade})
              </option>
            ))}
          </select>
          <div style={{ fontSize: "0.72rem", color: "#52525B", marginTop: 6 }}>
            Ao selecionar, categoria/descrição/unidade e materiais são preenchidos automaticamente.
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {os.contrato_id && !editId && (
            <F label="Puxar da planilha do contrato" full>
              <select className="select" value={puxarId} onChange={(e) => puxarDoContrato(e.target.value)} data-testid="item-puxar-contrato">
                <option value="">— escolha um item do contrato (preenche tudo) —</option>
                {contratoItens.map((ci) => <option key={ci.id} value={ci.id}>{ci.codigo ? `${ci.codigo} · ` : ""}{ci.apelido || ci.descricao} · {ci.unidade}{ci.saldo != null ? ` · saldo ${Number(ci.saldo).toLocaleString("pt-BR")}` : ""}</option>)}
              </select>
              {puxarId && <div className="text-xs" style={{ color: "#0C4A6E", marginTop: 4 }} data-testid="item-puxar-aviso">Item vinculado ao contrato: entra no boletim de medição e abate o saldo contratual.</div>}
            </F>
          )}
          {editId && os.contrato_id && (
            <F label="Item do contrato (vínculo para medição)" full>
              <select className="select" value={item.contrato_item_id || ""} onChange={(e) => setItem({ ...item, contrato_item_id: e.target.value || null })} data-testid="item-contrato-item">
                <option value="">— sem vínculo (não entra na medição) —</option>
                {contratoItens.map((ci) => <option key={ci.id} value={ci.id}>{ci.codigo ? `${ci.codigo} · ` : ""}{ci.apelido || ci.descricao} · {ci.unidade}</option>)}
              </select>
            </F>
          )}
          <F label="Categoria">
            <select className="select" value={item.categoria} onChange={(e) => setItem({ ...item, categoria: e.target.value })} data-testid="item-categoria">
              {["Vertical", "Horizontal", "Defensas", "Tachas", "Capina", "Demarcação", "Outros"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </F>
          <F label="Unidade">
            <input className="input" list="item-unidades" value={item.unidade} onChange={(e) => setItem({ ...item, unidade: e.target.value })} data-testid="item-unidade" />
            <datalist id="item-unidades">{["un", "m²", "m", "ml", "kg", "vb", "mês", "km", "h"].map((u) => <option key={u} value={u} />)}</datalist>
          </F>
          <F label="Descrição *" full>
            <input className="input" value={item.descricao} onChange={(e) => setItem({ ...item, descricao: e.target.value })} required data-testid="item-descricao" />
          </F>
          <F label="Apelido (nome curto para o campo)" full>
            <input className="input" value={item.apelido || ""} onChange={(e) => setItem({ ...item, apelido: e.target.value })} placeholder="ex.: Placa de aço SI+III — aparece no lugar da descrição longa nos apontamentos" maxLength={60} data-testid="item-apelido" />
          </F>
          <F label="Material">
            <input className="input" value={item.material} onChange={(e) => setItem({ ...item, material: e.target.value })} />
          </F>
          <F label="Cor">
            <input className="input" value={item.cor} onChange={(e) => setItem({ ...item, cor: e.target.value })} />
          </F>
          <F label="Dimensão">
            <input className="input" value={item.dimensao} onChange={(e) => setItem({ ...item, dimensao: e.target.value })} />
          </F>
          <F label="Quantidade planejada *">
            <input className="input" type="number" step="0.01" value={item.qtd_planejada} onChange={(e) => setItem({ ...item, qtd_planejada: e.target.value })} required data-testid="item-qtd" />
          </F>
        </div>

        {item.materiais && item.materiais.length > 0 && (
          <div style={{ marginTop: 20, padding: 16, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3 }}>
            <div className="overline mb-3">Materiais que serão calculados</div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8, fontSize: "0.75rem", fontWeight: 700, color: "#71717A", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
              <div>Insumo</div>
              <div style={{ textAlign: "right" }}>Coef. por {item.unidade}</div>
              <div style={{ textAlign: "right" }}>Total ({item.qtd_planejada || 0} {item.unidade})</div>
            </div>
            {item.materiais.map((m, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8, padding: "6px 0", borderTop: "1px dashed #E4E4E7" }}>
                <div style={{ fontWeight: 600 }}>{m.insumo_nome}</div>
                <div className="font-mono" style={{ textAlign: "right" }}>{m.quantidade_por_unidade} {m.unidade}</div>
                <div className="font-mono font-semibold" style={{ textAlign: "right" }}>
                  {((parseFloat(item.qtd_planejada) || 0) * m.quantidade_por_unidade).toFixed(2)} {m.unidade}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={saveItem} data-testid="btn-save-item">Salvar item</button>
        </div>
      </Modal>

      <style>{`
        @media (max-width: 900px) {
          .os-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <VincularContratoModal open={vincularModal} onClose={() => setVincularModal(false)} os={os} itens={os.itens || []} onDone={load} />
      {medicaoModal && (
        <div
          role="dialog"
          onClick={() => setMedicaoModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#FFF", width: "100%", maxWidth: 1100, maxHeight: "92vh", overflow: "auto", borderRadius: 4, padding: 24 }}
            data-testid="medicao-modal"
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div className="overline">Medição por Trecho · OS {os.numero}</div>
                <h2 className="font-display font-bold" style={{ fontSize: "1.4rem", marginTop: 4 }}>Apontamentos de pintura e placas com trecho</h2>
                <div style={{ fontSize: "0.8rem", color: "#71717A", marginTop: 4 }}>
                  {os.contratante} · {os.rodovia || "—"} {os.trecho ? "· " + os.trecho : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => exportarMedicaoExcel(os, apontamentos)}
                  data-testid="btn-export-medicao-excel"
                >📊 Excel</button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => window.print()}
                  data-testid="btn-print-medicao"
                >Imprimir</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setMedicaoModal(false)}>Fechar</button>
              </div>
            </div>

            {(() => {
              const byDate = (a, b) => (a.data_execucao || "").localeCompare(b.data_execucao || "");
              const ord = (apontamentos || []).filter((a) => a.pintura && !a.deleted).sort(byDate);
              const placas = (apontamentos || []).filter((a) => a.placa && !a.deleted).sort(byDate);
              if (ord.length === 0 && placas.length === 0) {
                return (
                  <div style={{ padding: 40, textAlign: "center", color: "#71717A" }} data-testid="medicao-vazio">
                    Nenhum apontamento de pintura ou placa por trecho registrado ainda.
                    <div style={{ fontSize: "0.8rem", marginTop: 8 }}>Use <strong>Apontar Pintura</strong> ou <strong>Apontar Placas</strong> no menu para começar.</div>
                  </div>
                );
              }
              const totalArea = ord.reduce((s, a) => s + (a.pintura?.area_m2 || a.quantidade || 0), 0);
              const totalExt = ord.reduce((s, a) => s + (a.pintura?.extensao_m || 0), 0);
              return (
                <>
                  {placas.length > 0 && <TabelaPlacas placas={placas} onZoom={setFotoZoom} onEdit={isAdmin ? setEditAp : null} />}

                  {ord.length > 0 && (
                  <section data-testid="medicao-secao-pintura">
                  <h3 className="font-display font-bold" style={{ fontSize: "1.05rem", marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #0A0A0A" }}>Sinalização Horizontal · Pintura</h3>
                  {/* Resumo topo */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
                    <CardResumo label="Trechos" value={ord.length} testId="pintura-total-trechos" />
                    <CardResumo label="Extensão total" value={totalExt.toLocaleString("pt-BR")} unit="m" testId="pintura-total-ext" />
                    <CardResumo label="Área total pintada" value={totalArea.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} unit="m²" destaque testId="pintura-total-m2" />
                  </div>

                  {/* Tabela de trechos */}
                  <div style={{ overflowX: "auto" }}>
                    <table className="pcp-table">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }}>Foto</th>
                          <th>Data</th>
                          <th>Trecho (km)</th>
                          <th>Extensão</th>
                          <th>Tipo</th>
                          <th>Método</th>
                          <th>Cor</th>
                          <th style={{ textAlign: "right" }}>Largura</th>
                          <th style={{ textAlign: "right" }}>Fator</th>
                          <th style={{ textAlign: "right" }}>Área m²</th>
                          <th>GPS</th>
                          <th>Fotos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ord.map((a) => {
                          const p = a.pintura || {};
                          return (
                            <tr key={a.id}>
                              <CelFoto a={a} onZoom={setFotoZoom} />
                              <td style={{ fontSize: "0.82rem" }}>{fmtData(a.data_execucao)}</td>
                              <td style={{ fontWeight: 600 }}>{p.km_inicial} → {p.km_final}</td>
                              <td className="font-mono" style={{ textAlign: "right" }}>{(p.extensao_m || 0).toLocaleString("pt-BR")}m</td>
                              <td style={{ fontSize: "0.82rem" }}>{p.tipo_label || p.tipo || "—"}</td>
                              <td><span className="chip" style={{ textTransform: "uppercase", fontSize: "0.7rem" }}>{p.metodo || "—"}</span></td>
                              <td style={{ fontSize: "0.82rem" }}>{p.cor || "—"}</td>
                              <td className="font-mono" style={{ textAlign: "right" }}>{p.largura_cm ? `${p.largura_cm}cm` : "—"}</td>
                              <td className="font-mono" style={{ textAlign: "right" }}>{(p.fator || 0).toFixed(2)}</td>
                              <td className="font-mono font-semibold" style={{ textAlign: "right" }}>{(p.area_m2 || a.quantidade || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</td>
                              <CelGps a={a} />
                              <CelVer a={a} onEdit={isAdmin ? setEditAp : null} />
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={9} style={{ textAlign: "right", fontWeight: 700, background: "#FAFAFA" }}>TOTAL</td>
                          <td className="font-mono font-semibold" style={{ textAlign: "right", background: "#FFFBEA" }}>{totalArea.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</td>
                          <td style={{ background: "#FAFAFA" }} />
                          <td style={{ background: "#FAFAFA" }} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  </section>
                  )}

                  <div style={{ marginTop: 12, padding: 10, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3, fontSize: "0.78rem", color: "#52525B" }}>
                    Este é o preview da Medição. A versão final (BDM) — com layout de assinatura do cliente, agrupamento por dia/equipe e export em Excel — sai na próxima fase.
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Zoom de foto quando clicar */}
      {fotoZoom && (
        <div
          role="dialog"
          onClick={() => setFotoZoom(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, cursor: "zoom-out" }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, maxHeight: "92vh", overflow: "auto" }}>
            {fotoZoom.map((f, i) => (
              <img key={i} src={f} alt="" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 4 }} />
            ))}
          </div>
        </div>
      )}

      {concluirModal && (
        <div
          role="dialog"
          onClick={() => !concluindo && setConcluirModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.75)", zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#FFF", width: "100%", maxWidth: 720, maxHeight: "92vh", overflow: "auto", borderRadius: 4, padding: 24 }}
            data-testid="concluir-modal"
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div className="overline">Conclusão da OS</div>
                <h2 className="font-display font-bold" style={{ fontSize: "1.35rem", marginTop: 4 }}>Concluir OS {os.numero}</h2>
                <div style={{ fontSize: "0.8rem", color: "#71717A", marginTop: 4 }}>
                  Ao concluir, o status muda para <strong>Concluída</strong> e é registrada a data de conclusão.
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setConcluirModal(false)} disabled={concluindo}>Cancelar</button>
            </div>

            <div style={{ padding: 14, background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 3, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#7A5D00" }}>
                <Warning size={16} weight="bold" /> Houve devolução de material para o estoque?
              </div>
              <div style={{ fontSize: "0.78rem", color: "#52525B", marginTop: 4 }}>
                Se sobrou material no canteiro que voltou para o almoxarifado, informe abaixo.
                Esse valor será <strong>subtraído do consumo real</strong> no relatório e retorna ao saldo do estoque.
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  className={`btn btn-sm ${temDevolucao === true ? "btn-primary" : "btn-outline"}`}
                  onClick={() => setTemDevolucao(true)}
                  data-testid="btn-dev-sim"
                >Sim, houve devolução</button>
                <button
                  type="button"
                  className={`btn btn-sm ${temDevolucao === false ? "btn-primary" : "btn-outline"}`}
                  onClick={() => setTemDevolucao(false)}
                  data-testid="btn-dev-nao"
                >Não houve devolução</button>
              </div>
            </div>

            {temDevolucao === true && (
              <div style={{ marginBottom: 16 }}>
                <div className="overline mb-1">Quantidade devolvida por insumo</div>
                {devolucoes.length === 0 ? (
                  <div style={{ padding: 12, background: "#FAFAFA", color: "#71717A", fontSize: "0.85rem", borderRadius: 3 }}>
                    Nenhum insumo foi consumido nesta OS. Vincule composições aos itens para calcular consumo.
                  </div>
                ) : (
                  <table className="pcp-table">
                    <thead>
                      <tr>
                        <th>Insumo</th>
                        <th style={{ textAlign: "right" }}>Consumo atual</th>
                        <th style={{ textAlign: "right", width: 140 }}>Devolvido</th>
                        <th>Un.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devolucoes.map((d, idx) => (
                        <tr key={d.insumo_id}>
                          <td style={{ fontWeight: 600 }}>{d.insumo_nome}</td>
                          <td className="font-mono" style={{ textAlign: "right", color: "#71717A" }}>
                            {d.disponivel.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                          </td>
                          <td>
                            <input
                              type="number" step="0.01" min="0" max={d.disponivel}
                              className="input"
                              value={d.quantidade}
                              onChange={(e) => setDevQtd(idx, e.target.value)}
                              data-testid={`dev-qtd-${idx}`}
                              style={{ textAlign: "right" }}
                            />
                          </td>
                          <td>{d.unidade}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <div className="label">Observação da conclusão (opcional)</div>
              <textarea
                className="input"
                value={obsConclusao}
                onChange={(e) => setObsConclusao(e.target.value)}
                placeholder="Ex.: obra entregue conforme escopo, sem intercorrências."
                rows={2}
                data-testid="obs-conclusao"
              />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #E4E4E7", paddingTop: 16 }}>
              <button className="btn btn-outline" onClick={() => setConcluirModal(false)} disabled={concluindo}>Cancelar</button>
              <button className="btn btn-primary" onClick={submitConclusao} disabled={concluindo || temDevolucao === null} data-testid="btn-confirmar-conclusao">
                <CheckCircle size={16} weight="bold" /> {concluindo ? "Concluindo…" : "Confirmar Conclusão"}
              </button>
            </div>
          </div>
        </div>
      )}

      {consumoModal && (
        <div
          role="dialog"
          onClick={() => setConsumoModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#FFF", width: "100%", maxWidth: 980, maxHeight: "92vh", overflow: "auto", borderRadius: 4, padding: 24 }}
            data-testid="consumo-modal"
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div className="overline">Relatório de Consumo · OS {os.numero}</div>
                <h2 className="font-display font-bold" style={{ fontSize: "1.4rem", marginTop: 4 }}>Insumos utilizados na obra</h2>
                <div style={{ fontSize: "0.8rem", color: "#71717A", marginTop: 4 }}>
                  {os.contratante} · {os.rodovia || "—"} {os.trecho ? "· " + os.trecho : ""} · Status: <strong>{os.status}</strong>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setConsumoModal(false)}>Fechar</button>
            </div>

            {consumoLoading || !consumo ? (
              <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>Calculando consumo…</div>
            ) : (
              <>
                {/* Resumo de serviços */}
                <div className="overline mb-1" style={{ marginTop: 8 }}>Serviços da obra</div>
                <table className="pcp-table" style={{ marginBottom: 20 }}>
                  <thead><tr><th>Categoria</th><th>Descrição</th><th style={{ textAlign: "right" }}>Planejado</th><th style={{ textAlign: "right" }}>Executado</th><th>Un.</th></tr></thead>
                  <tbody>
                    {consumo.itens.map((it) => (
                      <tr key={it.item_id}>
                        <td><span className="chip">{it.categoria}</span></td>
                        <td>{it.descricao}</td>
                        <td className="font-mono" style={{ textAlign: "right" }}>{it.qtd_planejada.toLocaleString("pt-BR")}</td>
                        <td className="font-mono font-semibold" style={{ textAlign: "right" }}>{it.qtd_executada.toLocaleString("pt-BR")}</td>
                        <td>{it.unidade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Consumo de insumos */}
                <div className="overline mb-1">Rendimento e consumo de insumos</div>
                {consumo.insumos.length === 0 ? (
                  <div style={{ padding: 20, color: "#71717A", background: "#FAFAFA", borderRadius: 3, fontSize: "0.85rem" }}>
                    Nenhum insumo vinculado às composições desta OS ainda. Vincule uma composição aos itens para ver o consumo.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="pcp-table">
                      <thead>
                        <tr>
                          <th rowSpan={2} style={{ verticalAlign: "bottom" }}>Insumo</th>
                          <th rowSpan={2} style={{ verticalAlign: "bottom" }}>Un.</th>
                          <th colSpan={2} style={{ textAlign: "center", background: "#FAFAFA" }}>Quantidade de insumo</th>
                          <th colSpan={2} style={{ textAlign: "center", background: "#FFFBEA" }}>Consumo específico</th>
                          <th rowSpan={2} style={{ verticalAlign: "bottom", textAlign: "right" }}>Extra</th>
                          <th rowSpan={2} style={{ verticalAlign: "bottom", textAlign: "right" }}>Devolvido</th>
                          <th rowSpan={2} style={{ verticalAlign: "bottom", textAlign: "right" }}>Variação vs prev.</th>
                        </tr>
                        <tr>
                          <th style={{ textAlign: "right", background: "#FAFAFA" }}>Previsto</th>
                          <th style={{ textAlign: "right", background: "#FAFAFA" }}>Enviado real</th>
                          <th style={{ textAlign: "right", background: "#FFFBEA" }}>Planejado</th>
                          <th style={{ textAlign: "right", background: "#FFFBEA" }}>Executado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consumo.insumos.map((r) => {
                          const cor = r.variacao_absoluta > 0 ? "#DC2626" : r.variacao_absoluta < 0 ? "#059669" : "#71717A";
                          const usv = r.unidade_servico || "un";
                          // Para KG/L/G etc: melhor é MENOR consumo (kg/m²). Para BD/UN: melhor é MAIOR rendimento (m²/BD).
                          const usaConsumo = r.metrica_preferida === "consumo";
                          const vPlan = usaConsumo ? r.consumo_planejado_por_servico : r.rendimento_planejado;
                          const vReal = usaConsumo ? r.consumo_real_por_servico : r.rendimento_real;
                          const delta = usaConsumo ? r.consumo_delta_pct : r.rendimento_delta_pct;
                          // Verde quando é "melhor": consumo menor OU rendimento maior
                          const isMelhor = usaConsumo ? delta < 0 : delta > 0;
                          const isPior = usaConsumo ? delta > 0 : delta < 0;
                          const rendCor = isMelhor ? "#059669" : isPior ? "#DC2626" : "#52525B";
                          const unidadeMetrica = usaConsumo ? `${r.unidade}/${usv}` : `${usv}/${r.unidade}`;
                          return (
                            <tr key={r.insumo_id}>
                              <td style={{ fontWeight: 600 }}>{r.insumo_nome}</td>
                              <td>{r.unidade}</td>
                              <td className="font-mono" style={{ textAlign: "right" }}>{r.planejado.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</td>
                              <td className="font-mono font-semibold" style={{ textAlign: "right" }}>{r.qtd_real_enviada.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</td>
                              <td className="font-mono" style={{ textAlign: "right" }}>
                                {vPlan > 0 ? vPlan.toLocaleString("pt-BR", { maximumFractionDigits: usaConsumo ? 3 : 2 }) : "—"}
                                <div style={{ fontSize: "0.7rem", color: "#71717A" }}>{unidadeMetrica}</div>
                              </td>
                              <td className="font-mono font-semibold" style={{ textAlign: "right", color: rendCor }}>
                                {vReal > 0 ? vReal.toLocaleString("pt-BR", { maximumFractionDigits: usaConsumo ? 3 : 2 }) : "—"}
                                <div style={{ fontSize: "0.7rem", color: rendCor }}>
                                  {vPlan > 0 && delta !== 0 ? (delta > 0 ? "+" : "") + delta + "%" : unidadeMetrica}
                                </div>
                              </td>
                              <td className="font-mono" style={{ textAlign: "right", color: r.extra > 0 ? "#B45309" : "#71717A" }}>
                                {r.extra > 0 ? "+" + r.extra.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : "0"}
                              </td>
                              <td className="font-mono" style={{ textAlign: "right", color: (r.devolvido || 0) > 0 ? "#059669" : "#71717A" }}>
                                {(r.devolvido || 0) > 0 ? "−" + (r.devolvido || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : "0"}
                              </td>
                              <td className="font-mono" style={{ textAlign: "right", color: cor }}>
                                {r.variacao_absoluta > 0 ? "+" : ""}{r.variacao_absoluta.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                                <div style={{ fontSize: "0.7rem" }}>{r.variacao_pct > 0 ? "+" : ""}{r.variacao_pct}%</div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ marginTop: 16, padding: 12, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3, fontSize: "0.78rem", color: "#52525B", lineHeight: 1.6 }}>
                  <div><strong>Consumo específico</strong> — a métrica adapta automaticamente à unidade do insumo:</div>
                  <div>• Insumos em <strong>KG / L / G</strong> → mostra <em>consumo por m²</em> (ex.: 0,30 kg/m²). <strong>Menor é melhor.</strong></div>
                  <div>• Insumos em <strong>BD / UN / PC</strong> → mostra <em>rendimento m²/unidade</em> (ex.: 40 m²/BD). <strong>Maior é melhor.</strong></div>
                  <div><strong>Verde</strong> = executado melhor que o previsto (economia); <strong>vermelho</strong> = pior (gasto acima).</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {editAp && <EditarApontamentoModal ap={editAp} equipes={equipes} itens={os.itens || []} onClose={() => setEditAp(null)} onSaved={() => load()} />}
    </>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed #E4E4E7" }}>
      <span style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "#71717A" }}>{label}</span>
      <span style={{ fontSize: "0.85rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
        {Icon && <Icon size={12} weight="bold" color="#71717A" />} {value}
      </span>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <div className="label">{label}</div>
      {children}
    </div>
  );
}
